import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import gsap from 'gsap';

// Inner component — all hooks live here (no early return above hooks)
const PreloaderContent = ({ children }) => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [percent, setPercent] = useState(0);
  const canvasRef = useRef(null);
  const preloaderRef = useRef(null);
  const logoRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0, active: false });

  // Handle route change transitions (duration exactly 3s)
  useEffect(() => {
    // Enforce scroll-to-top on route changes
    window.scrollTo(0, 0);

    // Show preloader overlay
    setLoading(true);
    setPercent(0);

    // Fade in preloader background if it was previously hidden
    if (preloaderRef.current) {
      gsap.set(preloaderRef.current, { opacity: 1, y: 0, pointerEvents: 'auto' });
    }

    // Animate percentage from 0 to 100 over 2.7s (leaving 300ms for exit animation)
    const obj = { value: 0 };
    const timeline = gsap.to(obj, {
      value: 100,
      duration: 2.7,
      ease: 'power1.inOut',
      onUpdate: () => {
        setPercent(Math.floor(obj.value));
      },
      onComplete: () => {
        // Exit animation (fading and sliding up)
        gsap.to(preloaderRef.current, {
          opacity: 0,
          y: -100,
          duration: 0.3,
          ease: 'power2.inOut',
          onComplete: () => {
            setLoading(false);
          },
        });
      },
    });

    // Elegant entry animation for the logo
    if (logoRef.current) {
      gsap.fromTo(
        logoRef.current,
        { scale: 0.8, opacity: 0, filter: 'blur(10px)' },
        { scale: 1, opacity: 1, filter: 'blur(0px)', duration: 0.8, ease: 'back.out(1.5)' }
      );
    }

    return () => {
      timeline.kill();
    };
  }, [location.pathname]);

  // Smoke Canvas Simulation
  useEffect(() => {
    if (!loading) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationFrameId;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Track mouse movement for interactive smoke
    const handleMouseMove = (e) => {
      mouseRef.current.x = e.clientX;
      mouseRef.current.y = e.clientY;
      mouseRef.current.active = true;
    };
    window.addEventListener('mousemove', handleMouseMove);

    // Smoke particles array
    let particles = [];

    class SmokeParticle {
      constructor(x, y, isLogoSmoke = false) {
        this.x = x;
        this.y = y;
        // Logo-emitted smoke is smaller and more energetic
        this.size = isLogoSmoke ? Math.random() * 40 + 20 : Math.random() * 90 + 50;
        this.vx = (Math.random() - 0.5) * (isLogoSmoke ? 2.5 : 1.2);
        this.vy = isLogoSmoke ? (Math.random() - 0.5) * 1.5 - 0.5 : -Math.random() * 1.8 - 1.0;
        this.opacity = Math.random() * 0.35 + 0.15;
        this.fade = Math.random() * 0.0025 + 0.0015;
        this.angle = Math.random() * Math.PI * 2;
        this.spin = (Math.random() - 0.5) * 0.015;
        
        // Dynamic colors: Gold gradient theme mixed with charcoal smoke
        const choice = Math.random();
        if (choice < 0.25) {
          this.color = { r: 242, g: 177, b: 53 }; // Brand Gold (#F2B135)
        } else if (choice < 0.5) {
          this.color = { r: 243, g: 202, b: 82 }; // Highlight Gold (#F3CA52)
        } else if (choice < 0.7) {
          this.color = { r: 140, g: 111, b: 10 }; // Warm Bronze
        } else {
          this.color = { r: 35, g: 35, b: 35 }; // Charcoal Gray
        }
      }

      update() {
        // Drifts away from the mouse pointer if mouse is nearby
        if (mouseRef.current.active) {
          const dx = this.x - mouseRef.current.x;
          const dy = this.y - mouseRef.current.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < 220) {
            const force = (220 - distance) / 220;
            const angle = Math.atan2(dy, dx);
            this.vx += Math.cos(angle) * force * 0.8;
            this.vy += Math.sin(angle) * force * 0.8;
          }
        }

        // Apply velocities
        this.x += this.vx;
        this.y += this.vy;
        this.size += 0.45; // Smoke expands slowly
        this.opacity -= this.fade;
        this.angle += this.spin;

        // Soft air friction
        this.vx *= 0.98;
        if (this.vy < -0.5) this.vy *= 0.98;
      }

      draw() {
        if (this.opacity <= 0) return;
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(this.angle);

        // Soft radial gradient for a realistic smoke look
        const grad = ctx.createRadialGradient(0, 0, this.size * 0.08, 0, 0, this.size);
        grad.addColorStop(0, `rgba(${this.color.r}, ${this.color.g}, ${this.color.b}, ${this.opacity})`);
        grad.addColorStop(0.4, `rgba(${this.color.r}, ${this.color.g}, ${this.color.b}, ${this.opacity * 0.4})`);
        grad.addColorStop(1, `rgba(${this.color.r}, ${this.color.g}, ${this.color.b}, 0)`);

        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(0, 0, this.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const cx = canvas.width / 2;
      const cy = canvas.height / 2;

      // Spawn general background smoke from bottom
      if (Math.random() < 0.25) {
        particles.push(new SmokeParticle(Math.random() * canvas.width, canvas.height + 60, false));
      }

      // Spawn ambient smoke around the center logo region
      if (Math.random() < 0.45) {
        const offsetRadius = 60;
        const angle = Math.random() * Math.PI * 2;
        const rx = cx + Math.cos(angle) * offsetRadius + (Math.random() - 0.5) * 40;
        const ry = cy + Math.sin(angle) * offsetRadius + (Math.random() - 0.5) * 40;
        particles.push(new SmokeParticle(rx, ry, true));
      }

      // Update and render all active smoke particles
      particles.forEach((p) => {
        p.update();
        p.draw();
      });

      // Keep array memory optimized by removing fully transparent particles
      particles = particles.filter((p) => p.opacity > 0);

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationFrameId);
    };
  }, [loading]);

  return (
    <>
      {loading && (
        <div
          ref={preloaderRef}
          style={{ zIndex: 99990 }}
          className="fixed inset-0 flex flex-col items-center justify-center bg-black overflow-hidden select-none pointer-events-auto transition-opacity"
        >
          {/* Smoke Canvas Overlay */}
          <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none opacity-70" />

          {/* Ambient Dark & Gold Background Vignettes */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-transparent to-black/95 pointer-events-none" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(242,177,53,0.08)_0%,rgba(0,0,0,0.92)_100%)] pointer-events-none" />

          {/* Loader UI Panel */}
          <div className="relative flex flex-col items-center z-10 text-center px-4">
            
            {/* Logo Structure with Spinners & Glow */}
            <div className="relative mb-8">
              {/* Soft Ambient Gold Blurs */}
              <div className="absolute -inset-8 rounded-full bg-gradient-to-r from-[#F2B135] via-amber-500 to-[#F3CA52] opacity-25 blur-2xl animate-pulse duration-[3000ms]" />
              <div className="absolute -inset-4 rounded-full bg-[#F2B135]/5 opacity-20 blur-xl" />

              {/* Glowing Orbit Rings */}
              <div className="absolute -inset-5 rounded-full border border-dashed border-[#F2B135]/30 animate-[spin_18s_linear_infinite]" />
              <div className="absolute -inset-3 rounded-full border border-dotted border-white/20 animate-[spin_10s_linear_infinite_reverse]" />

              {/* Logo Wrapper */}
              <div
                ref={logoRef}
                className="relative w-36 h-36 md:w-40 md:h-40 flex items-center justify-center bg-[#070707]/95 rounded-full border border-[#F2B135]/35 shadow-[0_0_40px_rgba(242,177,53,0.2)] p-6 overflow-hidden transition-all"
              >
                <img
                  src="/logo.png"
                  alt="Logo"
                  className="w-full h-full object-contain max-h-24 filter drop-shadow-[0_0_12px_rgba(242,177,53,0.5)]"
                  style={{
                    filter: 'invert(1) hue-rotate(180deg) brightness(2.2)',
                    mixBlendMode: 'screen',
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                {/* Fallback styled Logo */}
                <div className="hidden flex-col items-center justify-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#F2B135] to-[#F3CA52] rounded-lg flex items-center justify-center font-black text-black text-2xl shadow-[0_0_15px_rgba(242,177,53,0.4)]">
                    R
                  </div>
                  <span className="text-white font-extrabold text-[11px] tracking-widest mt-2 uppercase font-display">
                    Rojrz<span className="text-[#F2B135]">Tech</span>
                  </span>
                </div>
              </div>
            </div>

            {/* Titles & Typography */}
            <h2 className="text-2xl md:text-3xl font-black text-white tracking-[0.2em] uppercase mb-1 flex items-center justify-center gap-1">
              ROJRZ<span className="text-[#F2B135] drop-shadow-[0_0_8px_rgba(242,177,53,0.35)]">TECH</span>
            </h2>
            <p className="text-[10px] md:text-[11px] tracking-[0.35em] text-zinc-400 font-bold uppercase mb-10">
              INNOVATING THE DIGITAL FUTURE
            </p>

            {/* Elegant Gold Progress Indicator */}
            <div className="w-64 md:w-76 flex flex-col items-center">
              {/* Outer Sleek Line */}
              <div className="relative w-full h-[3px] bg-zinc-900/80 rounded-full overflow-hidden mb-3.5 border border-white/[0.03]">
                {/* Animated Progress Bar */}
                <div
                  className="absolute left-0 top-0 h-full bg-gradient-to-r from-[#F2B135] via-[#ffcc6b] to-[#F2B135] rounded-full shadow-[0_0_8px_rgba(242,177,53,0.7)] transition-all duration-75"
                  style={{ width: `${percent}%` }}
                />
              </div>

              {/* Details */}
              <div className="flex justify-between items-center w-full text-[10px] font-bold text-zinc-500 font-sans tracking-[0.25em] uppercase">
                <span className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#F2B135] animate-pulse" />
                  CONNECTING
                </span>
                <span className="text-[#F2B135] font-display text-xs tracking-normal">{percent}%</span>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Render children/page beneath preloader, handle visibility during loading */}
      <div className={loading ? 'h-screen overflow-hidden opacity-30 select-none pointer-events-none' : 'transition-opacity duration-500 opacity-100'}>
        {children}
      </div>
    </>
  );
};

// Outer wrapper — safely checks route BEFORE rendering PreloaderContent
const Preloader = ({ children }) => {
  const location = useLocation();

  // Admin pages: no preloader overlay at all — render children directly
  if (location.pathname.startsWith('/admin')) {
    return <>{children}</>;
  }

  return <PreloaderContent>{children}</PreloaderContent>;
};

export default Preloader;
